import React, { Fragment, useMemo } from 'react';
import clsx from 'clsx';
import { useTimer } from '@components-shared/src/countdown/hooks/useTimer';
import { GetNotifiedButton } from '@components/EmailNotifications/GetNotifiedButton';
import { Avatar } from '@components/basics/Images/Avatar';
import { Button } from '@components/main-components/button/Button';
import { CollectModal } from '@components/modals/Collect/CollectModal';
import { ReleaseMintsModal } from '@components/modals/ReleaseMintsModal';
import { ModalType, useModal } from '@context/modal';
import type { CollectModalFragmentDoc, FragmentType } from '@graphql/reactQuery';
import {
  getFragment,
  LandingHeroTopCollectorFragmentDoc,
  LandingHeroTopCollectorsFragmentDoc,
} from '@graphql/reactQuery';
import { trackClickEvent } from '@lib/analytics';
import { getSingularOrPlural } from '@utils/helpers';

const MAX_AVATARS_TO_SHOW = 3;
const MAX_NAMES_TO_SHOW = 2;

function CallToAction({
  mintStartTimestamp,
  artistId,
  artistUserId,
  releaseId,
  release,
}: {
  mintStartTimestamp: number;
  artistId: string;
  artistUserId: string;
  releaseId: string;
  release: FragmentType<CollectModalFragmentDoc>;
}) {
  const { openModal } = useModal();
  // forces update every second until start time
  useTimer({ expiryTimestamp: mintStartTimestamp });
  const isAfterMintStart = Date.now() > mintStartTimestamp;

  if (isAfterMintStart) {
    return (
      <Button
        onClick={() => {
          trackClickEvent({
            event: 'Collect Now Clicked',
            properties: {
              sourceComponent: 'Landing Hero',
              associationType: 'Release',
              associationId: releaseId,
            },
          });
          openModal(ModalType.COLLECT, {
            body: (
              <CollectModal
                release={release}
                playlistReferral={null}
                collectButtonSource="Landing Top Collectors"
              />
            ),
          });
        }}
        className="ml-auto p-2"
        variant="fuchsia"
        label="Collect"
        size="S"
      />
    );
  }

  return <GetNotifiedButton type="landing_hero" artistId={artistId} userId={artistUserId} />;
}

export const TopCollectorsMobile = ({
  release,
}: {
  release: FragmentType<LandingHeroTopCollectorsFragmentDoc>;
}) => {
  const { openModal } = useModal();

  const releaseData = getFragment(LandingHeroTopCollectorsFragmentDoc, release);
  const {
    id: releaseId,
    topCollectors,
    collectorsCount,
    mintStartTimestamp,
    artist: { id: artistId, userId: artistUserId },
  } = releaseData;

  const collectors = topCollectors
    .slice(0, MAX_AVATARS_TO_SHOW)
    .map(collector => getFragment(LandingHeroTopCollectorFragmentDoc, collector));

  const onCollectorsClick = () => {
    openModal(ModalType.RELEASE_MINTS, {
      body: <ReleaseMintsModal releaseId={releaseId} />,
    });
  };

  const [collectorAvatars, collectorNames] = useMemo(() => {
    return [
      [...collectors].reverse().map(({ user }, index) => (
        <div
          key={user.id}
          className={clsx({
            '-ml-3': index !== Math.min(collectorsCount, MAX_AVATARS_TO_SHOW) - 1,
          })}
        >
          <Avatar
            src={user.avatar?.url}
            size={32}
            borderRadius={32}
            webappUri={user.webappUri}
            walletAddress={user.publicAddress}
            hasDarkBorder
          />
        </div>
      )),

      collectors.slice(0, MAX_NAMES_TO_SHOW).map(({ user }, index) => (
        <Fragment key={user.id}>
          <span className="font-semibold text-base200" key={user.id}>
            {user.username}
          </span>
          {collectorsCount === MAX_NAMES_TO_SHOW && index === 0 && ' and '}
          {collectorsCount > MAX_NAMES_TO_SHOW && ', '}
          {collectorsCount > MAX_NAMES_TO_SHOW && index === MAX_NAMES_TO_SHOW - 1 && (
            <>
              {'and '}
              <span className="font-semibold text-base200">
                {collectorsCount - MAX_NAMES_TO_SHOW}
              </span>{' '}
              {getSingularOrPlural({
                count: collectorsCount - MAX_NAMES_TO_SHOW,
                word: 'other',
              })}
            </>
          )}
        </Fragment>
      )),
    ];
  }, [collectors, collectorsCount]);

  return (
    <div className="flex h-full  w-full items-center bg-base900">
      {collectorsCount === 0 ? (
        <p className="bg-base900 text-base-xs text-base400">Be the first to collect</p>
      ) : (
        <>
          <button onClick={onCollectorsClick} className="mr-3 flex flex-row-reverse items-center">
            {collectorAvatars}
          </button>
          <button onClick={onCollectorsClick} className="mr-4 text-left text-base-xs text-base400">
            {collectorNames} minted
          </button>
        </>
      )}
      <CallToAction
        mintStartTimestamp={mintStartTimestamp}
        artistId={artistId}
        artistUserId={artistUserId}
        releaseId={releaseId}
        release={releaseData}
      />
    </div>
  );
};
